import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../auth/sso/providers/authProvider';
import { useUserVerification } from '../auth/sso/hooks/useUserVerification';
import { useSAPCDC } from '../auth/sapcdc/hooks/useSAPCDC';
import { useAuth } from '../contexts/AuthContext';
import devInstanceAccessService from '../services/DevInstanceAccessService';
import { Button } from "./ui/button";
import Header from "./ui/Header";
import Footer from "./ui/Footer";
import { Eye, EyeClosed } from "lucide-react";
import Carousel from "./ui/Carousel";

const LandingPage = () => {
  const navigate = useNavigate();
  const { instance } = useMsal();
  
  // Notification state
  const [notification, setNotification] = useState({
    show: false,
    title: '',
    message: ''
  });
  
  // Helper function to show notification and refresh page
  const showNotificationAndRefresh = (title, message) => {
    setNotification({
      show: true,
      title: title,
      message: message
    });
  };
  
  // Handle OK button click in notification
  const handleNotificationOK = () => {
    setNotification({ show: false, title: '', message: '' });
    window.location.reload();
  };
  
  // Custom Notification Dialog Component
  const NotificationDialog = () => {
    if (!notification.show) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-8">
          <div className="text-center">
            {/* Red X Icon */}
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full border-2 border-red-500 mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            
            {/* Title */}
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              {notification.title}
            </h3>
            
            {/* Message */}
            <p className="text-gray-600 mb-6 leading-relaxed whitespace-pre-line">
              {notification.message}
            </p>
            
            {/* OK Button */}
            <button
              onClick={handleNotificationOK}
              className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-8 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              OK
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Loading Overlay Component with Spinner
  const LoadingOverlay = () => {
    if (!loadingOverlay.show) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-sm w-full mx-4">
          <div className="flex flex-col items-center">
            {/* Spinner */}
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-brand-blue mb-4"></div>
            
            {/* Status Message */}
            <p className="text-gray-700 text-lg font-medium text-center">
              {loadingOverlay.message}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const { verifyUserAccess, isVerifying } = useUserVerification();
  const { login: authLogin } = useAuth();
  const { 
    login: sapCDCLogin, 
    register: sapCDCRegister,
    isAuthenticated: isSAPCDCAuthenticated, 
    user: sapCDCUser, 
    isLoading: isSAPCDCLoading,
    error: sapCDCError
  } = useSAPCDC();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [authMethod, setAuthMethod] = useState('sapcdc'); // Default to SAP CDC authentication
  const [errors, setErrors] = useState({});
  const [loadingOverlay, setLoadingOverlay] = useState({
    show: false,
    message: ''
  });

  // ...existing code...

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email address is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters long';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSAPCDCLogin = async (e) => {
    e.preventDefault();
    
    // Validate form before proceeding
    if (!validateForm()) {
      return;
    }
    
    try {
      console.log('\n🚀 [Landing Page] Starting SAP CDC login with credentials...');
      console.log('📧 [Landing Page] Email:', formData.email);
      
      setAuthMethod('sapcdc');
      
      // Show loading overlay
      setLoadingOverlay({ show: true, message: 'Authenticating...' });
      
      // Use SAP CDC authentication with email and password
      const loginResult = await sapCDCLogin({
        loginID: formData.email,
        password: formData.password
      });
      
      if (loginResult.success && loginResult.user) {
        console.log('✅ [Landing Page] SAP CDC login successful');
        console.log('👤 [Landing Page] SAP CDC user:', {
          UID: loginResult.user.UID,
          email: loginResult.user.profile?.email,
          firstName: loginResult.user.profile?.firstName,
          lastName: loginResult.user.profile?.lastName,
          loginProvider: loginResult.user.loginProvider
        });
        
        // Extract user information
        const userEmail = loginResult.user.profile?.email || formData.email;
        const userName = `${loginResult.user.profile?.firstName || ''} ${loginResult.user.profile?.lastName || ''}`.trim() ||
                        loginResult.user.profile?.email?.split('@')[0] ||
                        formData.email.split('@')[0].split('.').map(name => 
                          name.charAt(0).toUpperCase() + name.slice(1)
                        ).join(' ');
        
        if (userEmail) {
          console.log('🔍 [Landing Page] Starting MongoDB user verification for SAP CDC user...');
          console.log('📧 [Landing Page] Verifying access for email:', userEmail);
          
          // Update loading message
          setLoadingOverlay({ show: true, message: 'Verifying Access...' });
          
          // Verify user access against MongoDB instances using SAP CDC token
          const verificationResult = await verifyUserAccess(userEmail, loginResult.token?.sessionToken);
          
          console.log('📊 [Landing Page] SAP CDC verification completed:', {
            isValid: verificationResult?.isValid,
            totalInstances: verificationResult?.totalInstances || 0,
            hasAccessibleInstances: verificationResult?.accessibleInstances?.length > 0
          });
          
          if (verificationResult && verificationResult.isValid) {
            console.log('✅ [Landing Page] SAP CDC user has access to Adapt instances');
            console.log(`✅ Welcome to Adapt Learning! SAP CDC Login successful for ${userName}. Access granted to ${verificationResult.totalInstances} instance(s).`);
            
            // Set authentication state in context
            authLogin({
              user: userName,
              email: userEmail,
              loginType: 'SAP CDC',
              accessibleInstances: verificationResult.accessibleInstances,
              verificationResult: verificationResult
            });
            
            // Hide loading overlay before navigation
            setLoadingOverlay({ show: false, message: '' });
            
            // Navigate to hub page (beta) or instances page, based on allowlist
            navigate(await devInstanceAccessService.getPostLoginRoute(userEmail));
          } else {
            console.log('❌ [Landing Page] SAP CDC user has no access to any Adapt instances');
            setLoadingOverlay({ show: false, message: '' });
            showNotificationAndRefresh(
              'Access Denied',
              `Authentication was successful, but no access is available to any Adapt Learning instances for ${userEmail}.\n\nPlease sign up as a new user to help your administrator provide access.`
            );
          }
        } else {
          console.error('❌ [Landing Page] No email found in SAP CDC user profile');
          setLoadingOverlay({ show: false, message: '' });
          showNotificationAndRefresh(
            'Profile Error',
            'No email address found in your profile.\n\nPlease contact your administrator.'
          );
        }
      } else {
        console.error('❌ [Landing Page] SAP CDC login failed:', loginResult.error);
        setLoadingOverlay({ show: false, message: '' });
        showNotificationAndRefresh(
          'Login Failed',
          'The email address or password you entered is incorrect.\n\nPlease try again.'
        );
      }
    } catch (error) {
      console.error('❌ [Landing Page] SAP CDC login error:', error);
      setLoadingOverlay({ show: false, message: '' });
      
      // Check if it's an "Invalid LoginID" error (user not registered)
      if (error.message && error.message.includes('Invalid LoginID')) {
        console.log('🔍 [Landing Page] User not found in SAP CDC system');
        
        const userChoice = window.confirm(`User ${formData.email} not found in SAP CDC system.

To create an account, you have two options:

1. Visit the registration portal (Recommended)
2. Register via API (Advanced)

Click OK to visit the registration portal, or Cancel to try different credentials.`);
        
        if (userChoice) {
          // Direct user to the proper registration portal
          const registrationURL = 'https://connect.laerdal.com';
          window.open(registrationURL, '_blank', 'width=800,height=600');
          
          console.log(`ℹ️ Registration required. Please complete registration at ${registrationURL} and then return here to login.

After registration:
1. Wait for the registration process to complete
2. Close the registration tab
3. Return to this page and login with your new credentials`);
        }
      }
      // Check if it's a CORS error
      else if (error.message && (
        error.message.includes('CORS') || 
        error.message.includes('Access-Control-Allow-Origin') ||
        error.message.includes('blocked by CORS policy') ||
        error.message.includes('webSdkBootstrap')
      )) {
        console.log('🚨 [Landing Page] CORS error detected');
        setLoadingOverlay({ show: false, message: '' });
        showNotificationAndRefresh(
          'CORS Error',
          'Unable to connect to authentication servers.\n\nSolution:\n1. Visit https://connect.laerdal.com first\n2. Wait for redirect to id.laerdal.com\n3. Return to localhost and try again\n\nThis establishes the necessary authentication cookies for localhost development.'
        );
      } else {
        setLoadingOverlay({ show: false, message: '' });
        showNotificationAndRefresh(
          'Login Error',
          `Login failed: ${error.message}.\n\nPlease check your credentials or contact your administrator.`
        );
      }
    } finally {
      setAuthMethod('sapcdc');
    }
  };

  const handleSSOLogin = async () => {
    try {
      console.log('\n🚀 [SSO Login] Starting Azure AD SSO login process...');
      console.log('🔐 [SSO Login] Login request configuration:', {
        scopes: loginRequest.scopes,
        prompt: loginRequest.prompt || 'default'
      });

      // Show loading overlay
      setLoadingOverlay({ show: true, message: 'Authenticating...' });

      // Perform popup login
      console.log('⏳ [SSO Login] Opening Azure AD popup...');
      const response = await instance.loginPopup(loginRequest);
      
      console.log('✅ [SSO Login] Azure AD authentication successful!');
      console.log('📋 [SSO Login] Authentication response received:', {
        hasAccount: !!response.account,
        hasAccessToken: !!response.accessToken,
        tokenType: response.tokenType || 'Bearer',
        expiresOn: response.expiresOn
      });
      
      if (response.account) {
        // Extract user information
        const userDisplayName = response.account.name || response.account.username;
        const userEmail = response.account.username;
        
        console.log('👤 [SSO Login] User details extracted:', {
          displayName: userDisplayName,
          email: userEmail,
          accountId: response.account.localAccountId,
          tenantId: response.account.tenantId
        });

        // Log the token details
        console.log('🎟️ [SSO Login] Access token details:', {
          scopes: response.scopes || [],
          tokenLength: response.accessToken ? response.accessToken.length : 0,
          hasToken: !!response.accessToken
        });
        
        // Update loading message
        setLoadingOverlay({ show: true, message: 'Verifying Access...' });
        
        // Verify user access against MongoDB instances
        console.log('\n🔍 [SSO Login] Starting MongoDB user verification...');
        console.log('📧 [SSO Login] Verifying access for email:', userEmail);
        
        const verificationResult = await verifyUserAccess(userEmail, response.accessToken);
        
        console.log('📊 [SSO Login] Verification completed:', {
          isValid: verificationResult?.isValid,
          totalInstances: verificationResult?.totalInstances || 0,
          hasAccessibleInstances: verificationResult?.accessibleInstances?.length > 0
        });
        
        if (verificationResult && verificationResult.isValid) {
          console.log('✅ [SSO Login] User has access to Adapt instances');
          console.log('📋 [SSO Login] Accessible instances:', verificationResult.accessibleInstances.map(inst => ({
            id: inst.id,
            name: inst.name,
            userRole: inst.userRole
          })));
          
          // Log success message
          console.log(`✅ Welcome to Adapt Learning! SSO Login successful for ${userDisplayName}. Access granted to ${verificationResult.totalInstances} instance(s).`);
          
          // Set authentication state in context
          authLogin({
            user: userDisplayName,
            email: userEmail,
            loginType: 'Azure AD SSO',
            accessibleInstances: verificationResult.accessibleInstances,
            verificationResult: verificationResult
          });
          
          // Hide loading overlay before navigation
          setLoadingOverlay({ show: false, message: '' });
          
          // Navigate to hub page (beta) or instances page, based on allowlist
          navigate(await devInstanceAccessService.getPostLoginRoute(userEmail));
        } else {
          console.log('❌ [SSO Login] User has no access to any Adapt instances');
          
          // Set authentication state even without access
          authLogin({
            user: userDisplayName,
            email: userEmail,
            loginType: 'Azure AD SSO',
            accessibleInstances: [],
            verificationResult: verificationResult
          });
          
          // Hide loading overlay before navigation
          setLoadingOverlay({ show: false, message: '' });
          
          // No accessible instances - always go straight to /instances (shows the
          // empty state), regardless of hub-beta eligibility; the hub page has no
          // "you have no access" state of its own.
          navigate('/instances');
        }
      }
    } catch (error) {
      console.error('❌ [SSO Login] SSO Login failed:', error);
      console.log('🔍 [SSO Login] Error details:', {
        errorCode: error.errorCode,
        errorMessage: error.errorMessage,
        name: error.name,
        stack: error.stack
      });
      
      setLoadingOverlay({ show: false, message: '' });
      
      if (error.errorCode === 'user_cancelled') {
        console.log('👋 [SSO Login] User cancelled the login flow');
        showNotificationAndRefresh(
          'Login Cancelled',
          'Login was cancelled.\n\nPlease try again if you wish to continue.'
        );
      } else {
        console.error('💥 [SSO Login] Unexpected error during login:', error);
        showNotificationAndRefresh(
          'Login Error',
          'Login failed.\n\nPlease try again or contact your administrator.'
        );
      }
    }
  };

  return (
    <section className="min-h-screen bg-white flex flex-col justify-between">
      <Header />
      {/* Custom Notification Dialog */}
      <NotificationDialog />
      {/* Loading Overlay */}
      <LoadingOverlay />
      {/* Main Login Container */}
      <div className="flex flex-1 items-stretch justify-center bg-white">
        {/* Left Panel - Image & Info */}
  <Carousel />

        {/* Right Panel - Login Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
          <div className="w-full max-w-md">
            {/* Header Section */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome Back</h1>
              <p className="text-gray-600 mb-4">Sign in to your account to continue</p>
            </div>
            <form onSubmit={handleSAPCDCLogin} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-left text-sm font-medium text-gray-700 mb-2">
                  Email Address <span className="text-error-500">*</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                  className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-primary-400 focus:border-primary-400 outline-none transition-colors ${errors.email ? 'border-error-500' : 'border-gray-300'}`}
                />
                {errors.email && <p className="text-error-500 text-sm mt-1 text-left">{errors.email}</p>}
              </div>
              <div className="relative">
                <label htmlFor="password" className="block text-left text-sm font-medium text-gray-700 mb-2">
                  Password <span className="text-error-500">*</span>
                </label>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Enter your password"
                  className={`w-full px-4 py-3 pr-12 border rounded-md focus:ring-2 focus:ring-primary-400 focus:border-primary-400 outline-none transition-colors ${errors.password ? 'border-error-500' : 'border-gray-300'}`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-11 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <Eye className="w-5 h-5" /> : <EyeClosed className="w-5 h-5" />}
                </button>
                {errors.password && <p className="text-error-500 text-sm mt-1 text-left">{errors.password}</p>}
              </div>
              <div className="flex items-center justify-between mt-2 mb-2">
                <label className="flex items-center text-sm text-gray-700">
                  <input type="checkbox" className="mr-2" /> Remember me
                </label>
                <button 
                  type="button" 
                  onClick={() => navigate('/forgot-password')}
                  className="text-brand-blue hover:text-primary-600 text-sm underline font-medium"
                >
                  Forgot password?
                </button>
              </div>
              <Button
                type="submit"
                disabled={loadingOverlay.show}
                className="w-full text-white py-3 text-lg font-semibold rounded-md transition-colors mt-4 bg-brand-blue hover:bg-brand-darkBlue border-none shadow-sm"
                style={{ 
                  height: '50px', 
                  backgroundColor: loadingOverlay.show ? '#6B7280' : '#2E7FA1',
                  fontSize: '18px', 
                  boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.04)',
                  color: 'white',
                  border: 'none'
                }}
              >
                Sign In
              </Button>
              <div className="flex items-center my-2">
                <div className="flex-grow border-t border-gray-300"></div>
                <span className="mx-3 text-gray-400 text-sm font-medium">Or</span>
                <div className="flex-grow border-t border-gray-300"></div>
              </div>
              <Button
                onClick={handleSSOLogin}
                variant="outline"
                disabled={loadingOverlay.show}
                className="w-full border-2 border-brand-blue text-brand-blue py-3 text-lg font-semibold rounded-md transition-colors flex items-center justify-center bg-white hover:bg-primary-50 shadow-sm"
                style={{ 
                  height: '50px', 
                  fontSize: '18px',
                  borderColor: '#2E7FA1',
                  color: '#2E7FA1',
                  backgroundColor: 'white'
                }}
              >
                Sign in with SSO
              </Button>
              <div className="text-center mt-6">
                <span className="text-gray-500 text-base">Don’t have an account? </span>
                <button type="button" className="text-brand-blue hover:underline font-semibold text-base" onClick={() => navigate('/signup')}>Sign Up</button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </section>
  );
}

export default LandingPage;
